import { HttpParams } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { environment } from '@environments/environments';
import { OfficeTenantsService } from '@office/tenants/office-tenants.service';
import { AuthSettingSlugs } from '@shared/constants/website-settings-slugs.type';
import { CustomWebsiteSettingsView } from '@shared/models/domain/custom-views.model';
import { SettingsWebsiteView, WebsiteSettingsView } from '@shared/models/domain/domain-view.model';
import { Setting } from '@shared/models/domain/domain.model';
import { PageRequest } from '@shared/models/request/page-request.model';
import { PageResponse } from '@shared/models/response/page-response.model';
import { CoreApiService } from '@shared/services/common/core-api.service';
import { BehaviorSubject, Observable, map } from 'rxjs';

@Injectable({providedIn: 'root'})
export class SettingsService extends CoreApiService<Setting>{
    constructor() {
        super('settings');
    }
}

@Injectable({providedIn: 'root'})
export class WebsiteSettingsViewService extends CoreApiService<WebsiteSettingsView>{
    private officeTenantsService = inject(OfficeTenantsService);
    private subjectWebsiteSettings = new BehaviorSubject<Record<string, CustomWebsiteSettingsView> | null>(null);
    private authSettingsLoaded = false;
    private readonly authSlugs = Object.values(AuthSettingSlugs);
    authPageSettings = signal<Record<string, CustomWebsiteSettingsView>>({});
    authSettingsLoading = signal(false);

    constructor() {
        super('website-settings-view');
    }

    get currentWebsiteSettings$() {
        return this.subjectWebsiteSettings.asObservable();
    }

    loadAuthSettings(): void {
        if (this.authSettingsLoaded) return;
        const websiteId = this.officeTenantsService.getTenantDetailsBySlug()?.tenantId ?? environment.defaultWebsiteId;
        if (!websiteId) return;
        this.authSettingsLoaded = true;
        this.authSettingsLoading.set(true);
        this.getSettingsBySlugs(websiteId, this.authSlugs).subscribe({
            next: (settings) => {
                this.authPageSettings.set(settings);
                this.authSettingsLoading.set(false);
            },
            error: () => {
                this.authSettingsLoaded = false;
                this.authSettingsLoading.set(false);
            }
        });
    }

    websiteSetting(slug: AuthSettingSlugs): CustomWebsiteSettingsView | null {
        return this.authPageSettings()[slug] || null;
    }

    getWebsiteSettings(websiteId: number, noObservableEmit?:boolean): Observable<PageResponse<CustomWebsiteSettingsView>> {
        return this.httpService.get<PageResponse<CustomWebsiteSettingsView>>(`${this.baseUrl}api/${this.endpoint}/website/${websiteId}`).pipe(
            map((res) => {
                const obj = res.items.reduce((acc, cur) => {
                    acc[cur.settingSlug] = cur;
                    return acc;
                }, {} as Record<string, CustomWebsiteSettingsView>);
                if (!noObservableEmit) {
                    this.subjectWebsiteSettings.next(obj)
                };
                return res;
            })
        );
    }

    getSettingsBySlugs(websiteId: number, slugs: string[]): Observable<Record<string, CustomWebsiteSettingsView>> {
        const request = new PageRequest();
        request.find['settingSlug:in'] = slugs.join(',');
        if (websiteId) request.find['websiteId'] = websiteId;
        return this.httpService.get<PageResponse<CustomWebsiteSettingsView>>(`${this.baseUrl}api/${this.endpoint}`, { params: request.toParams() }).pipe(
            map((res) => res.items.reduce((acc: Record<string, CustomWebsiteSettingsView>, cur: CustomWebsiteSettingsView) => {
                acc[cur.settingSlug] = cur;
                return acc;
            }, {} as Record<string, CustomWebsiteSettingsView>))
        );
    }

    getPricingWebsiteSettings(websiteId: number = environment.defaultWebsiteId, pricingModel?: number, jobType?: number, driverUserId?: string): Observable<Record<string, WebsiteSettingsView>> {
        let params = new HttpParams();
        if (pricingModel) {
            params = params.set('pricingModel', pricingModel.toString());
        }
        if (jobType) {
            params = params.set('jobType', jobType.toString());
        }
        if (driverUserId) {
            params = params.set('driverUserId', driverUserId);
        }

        return this.httpService.get<PageResponse<WebsiteSettingsView>>(`${this.baseUrl}api/${this.endpoint}/website/${websiteId}/pricing`, {params}).pipe(
            map((res) => {
                const obj = res.items.reduce((acc, cur) => {
                    acc[cur.settingSlug] = cur;
                    return acc;
                }, {} as Record<string, WebsiteSettingsView>);
                return obj;
            })
        );
    }

}

@Injectable({providedIn: 'root'})
export class SettingsWebsiteViewService extends CoreApiService<SettingsWebsiteView>{
    constructor() {
        super('settings-website-view');
    }
}
